import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import ErrorBoundary from './components/ErrorBoundary'
import App from './App'
import api, { uploadPinPhoto } from './services/api'
import { installReplayListener } from './services/offlineQueue'
import { installPhotoFlushListener } from './lib/photoQueue'
import './index.css'

// Sentry initialization. DSN is baked at build time via VITE_SENTRY_DSN.
// When unset (local dev without Sentry wiring) we skip init entirely so
// there's no network chatter and no noise in the console.
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',
    release: import.meta.env.VITE_SENTRY_RELEASE,
    tracesSampleRate: 0.1,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
  })
}

// SW is registered in index.html; wire up the offline-queue replay listener
installReplayListener(api)
// ...and the binary photo queue (pasture pins) — flushes on reconnect.
installPhotoFlushListener(uploadPinPhoto)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
    mutations: {
      // The offline write queue (services/offlineQueue.js) handles offline
      // itself: a queued mutation stashes the write in IndexedDB and resolves
      // immediately. React Query's default networkMode:'online' would instead
      // PAUSE a mutation while offline — the mutationFn never runs, so the
      // submit button hangs on "Saving…" forever and nothing is queued.
      // 'always' lets the mutationFn run regardless of connectivity so the
      // queue can engage.
      networkMode: 'always',
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
)
